import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export type ScaffolderEnvironment = {
  id: string;
  name: string;
};

export type EnvironmentNamingConvention = {
  id: string;
  name: string;
  description?: string;
  default?: boolean;
  environments: ScaffolderEnvironment[];
};

export type DeploymentContextNamingMode = 'short' | 'canonical';

export type DeploymentContextNamingConvention = {
  id: string;
  name: string;
  description?: string;
  default?: boolean;
  environmentNameMode: DeploymentContextNamingMode;
  locationNameMode: DeploymentContextNamingMode;
};

export type DeploymentContextEnvironment = {
  canonicalName: string;
  displayName: string;
  shortName: string;
};

export type DeploymentContextLocation = {
  canonicalName: string;
  displayName: string;
  shortName: string;
};

export type DeploymentContextOptions = {
  namingConventions: DeploymentContextNamingConvention[];
  environments: DeploymentContextEnvironment[];
  locations: DeploymentContextLocation[];
};

function isNamingMode(value: unknown): value is DeploymentContextNamingMode {
  return value === 'short' || value === 'canonical';
}

export interface BeehiveScaffolderEnvironmentsApi {
  listEnvironments(): Promise<ScaffolderEnvironment[]>;
  listEnvironmentNamingConventions(): Promise<EnvironmentNamingConvention[]>;
  listDeploymentContextOptions(): Promise<DeploymentContextOptions>;
}

export const beehiveScaffolderEnvironmentsApiRef =
  createApiRef<BeehiveScaffolderEnvironmentsApi>({
    id: 'plugin.beehive-scaffolder.environments',
  });

export class BeehiveScaffolderEnvironmentsClient
  implements BeehiveScaffolderEnvironmentsApi
{
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  async listEnvironments(): Promise<ScaffolderEnvironment[]> {
    const baseUrl = await this.options.discoveryApi.getBaseUrl(
      'beehive-scaffolder',
    );
    const response = await this.options.fetchApi.fetch(
      `${baseUrl}/environments`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load environments: ${response.status} ${response.statusText}`,
      );
    }

    const data: unknown = await response.json();

    if (!Array.isArray(data)) {
      throw new Error('Environments response must be an array');
    }

    return data.map((item, index) => {
      if (
        typeof item !== 'object' ||
        item === null ||
        typeof (item as ScaffolderEnvironment).id !== 'string' ||
        typeof (item as ScaffolderEnvironment).name !== 'string'
      ) {
        throw new Error(
          `Environments response item ${index} must contain string id and name`,
        );
      }

      return {
        id: (item as ScaffolderEnvironment).id,
        name: (item as ScaffolderEnvironment).name,
      };
    });
  }

  async listEnvironmentNamingConventions(): Promise<
    EnvironmentNamingConvention[]
  > {
    const baseUrl = await this.options.discoveryApi.getBaseUrl(
      'beehive-scaffolder',
    );
    const response = await this.options.fetchApi.fetch(
      `${baseUrl}/environment-naming-conventions`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load environment naming conventions: ${response.status} ${response.statusText}`,
      );
    }

    const data: unknown = await response.json();

    if (!Array.isArray(data)) {
      throw new Error(
        'Environment naming conventions response must be an array',
      );
    }

    return data.map((item, index) => {
      if (
        typeof item !== 'object' ||
        item === null ||
        typeof (item as EnvironmentNamingConvention).id !== 'string' ||
        typeof (item as EnvironmentNamingConvention).name !== 'string' ||
        !Array.isArray((item as EnvironmentNamingConvention).environments)
      ) {
        throw new Error(
          `Environment naming conventions response item ${index} must contain string id, string name, and environments array`,
        );
      }

      const convention = item as EnvironmentNamingConvention;

      if (
        convention.description !== undefined &&
        typeof convention.description !== 'string'
      ) {
        throw new Error(
          `Environment naming conventions response item ${index} description must be a string`,
        );
      }

      if (
        convention.default !== undefined &&
        typeof convention.default !== 'boolean'
      ) {
        throw new Error(
          `Environment naming conventions response item ${index} default must be a boolean`,
        );
      }

      return {
        id: convention.id,
        name: convention.name,
        ...(convention.description
          ? { description: convention.description }
          : {}),
        ...(convention.default !== undefined
          ? { default: convention.default }
          : {}),
        environments: convention.environments.map(
          (environment, environmentIndex) => {
            if (
              typeof environment !== 'object' ||
              environment === null ||
              typeof environment.id !== 'string' ||
              typeof environment.name !== 'string'
            ) {
              throw new Error(
                `Environment naming conventions response item ${index} environment ${environmentIndex} must contain string id and name`,
              );
            }

            return {
              id: environment.id,
              name: environment.name,
            };
          },
        ),
      };
    });
  }

  async listDeploymentContextOptions(): Promise<DeploymentContextOptions> {
    const baseUrl = await this.options.discoveryApi.getBaseUrl(
      'beehive-scaffolder',
    );
    const response = await this.options.fetchApi.fetch(
      `${baseUrl}/deployment-context-options`,
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load deployment context options: ${response.status} ${response.statusText}`,
      );
    }

    const data: unknown = await response.json();

    if (typeof data !== 'object' || data === null) {
      throw new Error('Deployment context options response must be an object');
    }

    const options = data as DeploymentContextOptions;

    if (
      !Array.isArray(options.namingConventions) ||
      !Array.isArray(options.environments) ||
      !Array.isArray(options.locations)
    ) {
      throw new Error(
        'Deployment context options response must contain namingConventions, environments, and locations arrays',
      );
    }

    return {
      namingConventions: options.namingConventions.map((item, index) => {
        if (
          typeof item !== 'object' ||
          item === null ||
          typeof item.id !== 'string' ||
          typeof item.name !== 'string' ||
          !isNamingMode(item.environmentNameMode) ||
          !isNamingMode(item.locationNameMode)
        ) {
          throw new Error(
            `Deployment context naming convention ${index} must contain string id, string name, environmentNameMode, and locationNameMode`,
          );
        }

        if (
          item.description !== undefined &&
          typeof item.description !== 'string'
        ) {
          throw new Error(
            `Deployment context naming convention ${index} description must be a string`,
          );
        }

        if (item.default !== undefined && typeof item.default !== 'boolean') {
          throw new Error(
            `Deployment context naming convention ${index} default must be a boolean`,
          );
        }

        return {
          id: item.id,
          name: item.name,
          ...(item.description ? { description: item.description } : {}),
          ...(item.default !== undefined ? { default: item.default } : {}),
          environmentNameMode: item.environmentNameMode,
          locationNameMode: item.locationNameMode,
        };
      }),
      environments: options.environments.map((item, index) => {
        if (
          typeof item !== 'object' ||
          item === null ||
          typeof item.canonicalName !== 'string' ||
          typeof item.displayName !== 'string' ||
          typeof item.shortName !== 'string'
        ) {
          throw new Error(
            `Deployment context environment ${index} must contain string canonicalName, displayName, and shortName`,
          );
        }

        return {
          canonicalName: item.canonicalName,
          displayName: item.displayName,
          shortName: item.shortName,
        };
      }),
      locations: options.locations.map((item, index) => {
        if (
          typeof item !== 'object' ||
          item === null ||
          typeof item.canonicalName !== 'string' ||
          typeof item.displayName !== 'string' ||
          typeof item.shortName !== 'string'
        ) {
          throw new Error(
            `Deployment context location ${index} must contain string canonicalName, displayName, and shortName`,
          );
        }

        return {
          canonicalName: item.canonicalName,
          displayName: item.displayName,
          shortName: item.shortName,
        };
      }),
    };
  }
}
